// FormEditor — the Form block editor (a NEW Build-Menu block, Premium tier).
// Fable Hail Mary Wave 5 (Lane-N, BLOCK-FORMS). Deck source of truth:
// concepts/builder-blocks/05-form-block + 07/08-form-responses + index.md.
//
// Deck-mandated feature set (05): Form title; a field list where each field has a
// TYPE chip + a REQUIRED toggle, laid out in a fixed grip/icon/label/required/
// remove grid; "Add field" + the six field-type chips (Text · Email · Phone ·
// Dropdown · Checkbox · Date); Submit-button styling (label "Send" + the UNIVERSAL
// color component); "Email me each response" toggle. Preview renders the live form.
//
// PREMIUM: same gate model as the Poll block — response STORAGE + the owner
// Responses view are SERVER-checked, fail-closed (BE enforcement via
// SubscriptionService). The authoring surface ships unflagged (Gordon's flag
// policy); the heading carries the tier note and the upgrade path is the app's
// openUpgradePopup, wired at the block level.
//
// Controlled: `value` (form model) + `onChange(next)`.
//
// ISP-9999 (gordon-0915-form-block-ten-fixes) touched this file for four of the
// ten items:
//   (2) a seventh field type, "Long answer" (multi-line, 200-char limit) — just
//       one more entry in FIELD_TYPES; the "+" chip row and the type <select>
//       both already map over that array, so no new UI plumbing was needed.
//   (3) a per-field options editor for "Dropdown" fields (add / rename / remove
//       / drag-reorder, min 1 option) — reuses the SAME react-dnd useDrag/useDrop
//       pairing PollEditor.jsx's option rows use, scoped to one field's own
//       option list via a `fieldId` on the drag item so two different dropdown
//       fields' option lists can never cross-reorder each other.
//   (4) the "Advanced options" accordion wrapped "Submit button" + "Responses" —
//       Gordon: "the ADVANCED OPTIONS section is empty -> remove it entirely."
//       Removed the <AdvancedOptions> wrapper; both sections now render as
//       always-visible top-level EditorSections, same as "Basic" above them.
//       (Checked PollEditor's own Advanced options too, per the note's
//       instruction — it holds real "Allow multiple votes"/"Show results"
//       toggles, not empty, so it is deliberately left untouched here.)
//   (9) a per-field "Placeholder" control, independent of the label, for the
//       field types that render one (text/long answer/email/phone) — with a
//       type-level default shown as the control's OWN placeholder hint.

import React, { useState } from "react";
import styled from "styled-components";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import { TextInputField, Toggle, QuadStepper } from "../controls";
import ColorPopup from "../../Shared/ColorPopup";
import { maxLength, validateField } from "../validation";
import { fontFamily } from "../../../../theme/tokens";
import { fieldChrome } from "../EditorField";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const BORDER = fieldChrome.border;
const PURPLE = fieldChrome.accent;

const TITLE_MAX = 120;
const LABEL_MAX = 80;
const MAX_FIELDS = 20;
const DROPDOWN_OPTION_MAX = 40;
// ISP-9999 (item 3): "min 1 option" per Gordon's note — a dropdown field must
// always keep at least one option, even though Poll's own MIN_OPTIONS is 2.
const MIN_DROPDOWN_OPTIONS = 1;
// Scopes the react-dnd type: a field-row drag reorder isn't part of this
// note's nine items (only the dropdown OPTIONS' drag-reorder is), so this is
// the only DnD wiring FormEditor needs.
const DROPDOWN_OPTION_DRAG_TYPE = "form-editor-dropdown-option";

// The seven field types the deck + this note name, in order. "Long answer"
// (ISP-9999 item 2) sits right after "Text" — same input family, multi-line.
export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "longtext", label: "Long answer" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
];
const TYPE_LABEL = FIELD_TYPES.reduce((m, t) => ({ ...m, [t.value]: t.label }), {});

// ISP-9999 (item 9): per-field-type placeholder defaults. Only the types that
// actually render a text placeholder get an entry — dropdown always shows the
// fixed "Choose…" placeholder (not user-editable) and checkbox/date have none.
export const DEFAULT_PLACEHOLDER_BY_TYPE = {
  text: "Your answer",
  longtext: "Your answer",
  email: "you@example.com",
  phone: "(555) 000-0000",
};
const PLACEHOLDER_TYPES = new Set(Object.keys(DEFAULT_PLACEHOLDER_BY_TYPE));

export const FORM_DEFAULT = {
  title: "",
  fields: [
    { id: "f-name", label: "Name", type: "text", required: true },
    { id: "f-email", label: "Email", type: "email", required: true },
  ],
  submitLabel: "Send", // deck: Submit button label
  // ISP-9999: default the accent (submit button + field border/focus ring) to
  // the segment color, like every other color-controllable block. Resolved to
  // a real hex by usePaletteColor before it reaches FormLiveView/FormBlock; a
  // user override overwrites this with a literal hex.
  submitColor: "suggested_color_0",
  emailEachResponse: true, // "Email me each response"
  // fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458: Form
  // had no margin prop/control at all — see the Spacing section below.
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
};

const FormGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="3.5" width="16" height="17" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7.5 8h9M7.5 12h9M7.5 16h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const PremiumNote = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 6px;
  background: #ede4fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  vertical-align: middle;
`;
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr auto auto 44px;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  font-family: ${fontFamily};
`;
const Grip = styled.span`
  display: inline-flex;
  color: ${MUTED};
  cursor: grab;
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; border-radius: 4px; }
`;
const TypeSelect = styled.select`
  min-height: 36px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  padding: 0 8px;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  color: #6c1fb8;
  background: #faf7fe;
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const ReqWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  cursor: pointer;
  input { width: 18px; height: 18px; accent-color: ${PURPLE}; }
`;
const IconBtn = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  color: ${MUTED};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: #f6f7f9; color: #d92d20; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;
const Chip = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border: 1px dashed ${PURPLE};
  border-radius: 8px;
  background: #faf7fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const Hint = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${MUTED};
  line-height: 1.4;
`;
// ISP-9999 (item 3): dropdown-options sub-editor, nested under a dropdown
// field's own row.
const SubRow = styled.div`
  margin-top: 6px;
  padding-left: 26px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr 40px;
  align-items: center;
  gap: 6px;
  font-family: ${fontFamily};
`;
const AddOptionBtn = styled.button`
  align-self: flex-start;
  min-height: 36px;
  padding: 0 12px;
  border: 1px dashed ${PURPLE};
  border-radius: 8px;
  background: #faf7fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PURPLE}; outline-offset: 2px; }
`;
const LiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
// ISP-9999 (item 9): compact caption in front of the per-field placeholder
// control — deliberately not an EditorField `label` (that would add its own
// margin/typography meant for a full-width field, too heavy for this dense row).
const SubCaption = styled.span`
  flex: none;
  min-width: 70px;
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
`;

const GripIcon = () => (
  <svg width="12" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <g fill="currentColor">
      <circle cx="5" cy="3" r="1.1" /><circle cx="9" cy="3" r="1.1" />
      <circle cx="5" cy="7" r="1.1" /><circle cx="9" cy="7" r="1.1" />
      <circle cx="5" cy="11" r="1.1" /><circle cx="9" cy="11" r="1.1" />
    </g>
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 7h14M10 7V5h4v2M6 7l1 12h10l1-12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

let fieldSeq = 100;
let dropdownOptionSeq = 100;
const newDropdownOption = () => ({ id: `dopt-${Date.now()}-${dropdownOptionSeq++}`, text: "" });
const newField = (type = "text") => {
  const base = { id: `f-${Date.now()}-${fieldSeq++}`, label: TYPE_LABEL[type] || "Field", type, required: false };
  if (type === "dropdown") base.options = [newDropdownOption(), newDropdownOption()];
  return base;
};

// Draggable dropdown-option row: same useDrag/useDrop pairing as PollEditor's
// OptionRowDraggable (drop target's `hover` reorders live as the dragged row
// passes over a sibling; the drag source's `end` fires once the drop settles).
// Scoped to ONE field's own option list via `fieldId` on the drag item, so
// dragging inside field A's dropdown options can never reorder field B's.
const DropdownOptionRowDraggable = ({ fieldId, id, index, moveOption, onDropped, children }) => {
  const ref = React.useRef(null);

  const [, drop] = useDrop({
    accept: DROPDOWN_OPTION_DRAG_TYPE,
    hover(dragged) {
      if (dragged.fieldId !== fieldId) return;
      if (dragged.index === index) return;
      moveOption(dragged.index, index);
      dragged.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DROPDOWN_OPTION_DRAG_TYPE,
    item: { fieldId, id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    end: (item) => onDropped(item.index),
  });

  drag(drop(ref));

  return (
    <OptionRow ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      {children}
    </OptionRow>
  );
};

// ISP-9999 (item 3): the dropdown field's own options editor — add / rename /
// remove / drag-reorder, min 1 option, stored on `field.options`.
const DropdownOptionsEditor = ({ field, fieldIdx, onChangeOptions }) => {
  const options = field.options?.length ? field.options : [newDropdownOption()];
  const [announcement, setAnnouncement] = useState("");

  const setOption = (id, text) =>
    onChangeOptions(options.map((o) => (o.id === id ? { ...o, text } : o)));
  const removeOption = (id) => onChangeOptions(options.filter((o) => o.id !== id));
  const addOption = () => {
    if (options.length >= DROPDOWN_OPTION_MAX) return;
    onChangeOptions([...options, newDropdownOption()]);
  };
  const moveOption = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= options.length) return;
    const next = options.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChangeOptions(next);
  };
  const announceMoved = (toIndex) =>
    setAnnouncement(`Option moved to position ${toIndex + 1} of ${options.length}.`);
  const moveByKey = (idx, dir) => {
    const toIndex = idx + dir;
    if (toIndex < 0 || toIndex >= options.length) return;
    moveOption(idx, toIndex);
    announceMoved(toIndex);
  };

  return (
    <SubRow>
      <LiveRegion aria-live="polite">{announcement}</LiveRegion>
      {options.map((o, idx) => (
        <DropdownOptionRowDraggable
          key={o.id}
          fieldId={field.id}
          id={o.id}
          index={idx}
          moveOption={moveOption}
          onDropped={announceMoved}
        >
          <Grip
            role="button"
            tabIndex={0}
            aria-label={`Reorder field ${fieldIdx + 1} option ${idx + 1}`}
            title="Drag to reorder — or press Alt+Up / Alt+Down"
            onKeyDown={(e) => {
              if (!e.altKey) return;
              if (e.key === "ArrowUp") { e.preventDefault(); moveByKey(idx, -1); }
              if (e.key === "ArrowDown") { e.preventDefault(); moveByKey(idx, +1); }
            }}
          >
            <GripIcon />
          </Grip>
          <TextInputField
            aria-label={`Field ${fieldIdx + 1} option ${idx + 1}`}
            value={o.text}
            onChange={(s) => setOption(o.id, s)}
            placeholder={`Option ${idx + 1}`}
          />
          <IconBtn
            type="button"
            aria-label={`Remove field ${fieldIdx + 1} option ${idx + 1}`}
            disabled={options.length <= MIN_DROPDOWN_OPTIONS}
            onClick={() => removeOption(o.id)}
          >
            <TrashIcon />
          </IconBtn>
        </DropdownOptionRowDraggable>
      ))}
      <div>
        <AddOptionBtn
          type="button"
          onClick={addOption}
          disabled={options.length >= DROPDOWN_OPTION_MAX}
        >
          + Add option
        </AddOptionBtn>
      </div>
    </SubRow>
  );
};

const FormEditor = ({
  value = FORM_DEFAULT,
  onChange = () => {},
  segmentName,
  onBack,
  status = "ready",
  onRetry,
  errorMessage,
}) => {
  const v = { ...FORM_DEFAULT, ...value, fields: value?.fields?.length ? value.fields : FORM_DEFAULT.fields };
  const patch = (p) => onChange({ ...v, ...p });

  const titleError = validateField(v.title, [
    maxLength(TITLE_MAX, `Keep the title under ${TITLE_MAX} characters.`),
  ]);

  const setField = (id, p) =>
    patch({ fields: v.fields.map((f) => (f.id === id ? { ...f, ...p } : f)) });
  const removeField = (id) =>
    patch({ fields: v.fields.filter((f) => f.id !== id) });
  const addField = (type) => {
    if (v.fields.length >= MAX_FIELDS) return;
    patch({ fields: [...v.fields, newField(type)] });
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= v.fields.length) return;
    const next = v.fields.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    patch({ fields: next });
  };
  const setMarginSide = (side, val) => patch({ margin: { ...v.margin, [side]: val } });

  return (
    // ISP-9999 (item 3): local DndProvider (same pattern PollEditor.jsx uses)
    // so the dropdown-options rows' drag source/drop target work both in the
    // isolated harness AND inside the live app (which already wraps itself in
    // one — nesting is harmless).
    <DndProvider backend={HTML5Backend}>
      <BlockEditorPane
        blockName="Form"
        segmentName={segmentName}
        glyph={<FormGlyph />}
        onBack={onBack}
        status={status}
        onRetry={onRetry}
        errorMessage={errorMessage}
      >
        <EditorSection heading={<span>Basic<PremiumNote>PREMIUM</PremiumNote></span>}>
          <TextInputField
            label="Form title"
            value={v.title}
            onChange={(s) => patch({ title: s })}
            error={titleError}
            counterMax={TITLE_MAX}
            placeholder="Get in touch…"
          />

          <div>
            <div style={{ fontFamily, fontSize: 12, fontWeight: 600, color: INK, marginBottom: 8 }}>
              Fields
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {v.fields.map((f, idx) => (
                <div key={f.id}>
                  <FieldRow>
                    <Grip
                      role="button"
                      tabIndex={0}
                      aria-label={`Reorder field ${idx + 1}`}
                      title="Drag to reorder — or use ↑/↓"
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp") { e.preventDefault(); move(idx, -1); }
                        if (e.key === "ArrowDown") { e.preventDefault(); move(idx, +1); }
                      }}
                    >
                      <GripIcon />
                    </Grip>
                    <TextInputField
                      aria-label={`Field ${idx + 1} label`}
                      value={f.label}
                      onChange={(s) => setField(f.id, { label: s })}
                      error={validateField(f.label, [maxLength(LABEL_MAX, `Under ${LABEL_MAX} characters.`)])}
                      placeholder="Field label"
                    />
                    <TypeSelect
                      aria-label={`Field ${idx + 1} type`}
                      value={f.type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        const fieldPatch = { label: f.label || TYPE_LABEL[nextType], type: nextType };
                        // Seed default options the first time a field becomes a
                        // Dropdown — never clobbers options already authored.
                        if (nextType === "dropdown" && !(f.options && f.options.length)) {
                          fieldPatch.options = [newDropdownOption(), newDropdownOption()];
                        }
                        setField(f.id, fieldPatch);
                      }}
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </TypeSelect>
                    <ReqWrap>
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        aria-label={`Field ${idx + 1} required`}
                        onChange={(e) => setField(f.id, { required: e.target.checked })}
                      />
                      Required
                    </ReqWrap>
                    <IconBtn
                      type="button"
                      aria-label={`Remove field ${idx + 1}`}
                      disabled={v.fields.length <= 1}
                      onClick={() => removeField(f.id)}
                    >
                      <TrashIcon />
                    </IconBtn>
                  </FieldRow>

                  {/* ISP-9999 (item 3): dropdown fields get their own options editor. */}
                  {f.type === "dropdown" && (
                    <DropdownOptionsEditor
                      field={f}
                      fieldIdx={idx}
                      onChangeOptions={(options) => setField(f.id, { options })}
                    />
                  )}

                  {/* ISP-9999 (item 9): placeholder is its OWN field, independent
                      of the label, for the types that render one. */}
                  {PLACEHOLDER_TYPES.has(f.type) && (
                    <SubRow style={{ flexDirection: "row", alignItems: "center" }}>
                      <SubCaption>Placeholder</SubCaption>
                      <div style={{ flex: 1 }}>
                        <TextInputField
                          aria-label={`Field ${idx + 1} placeholder`}
                          value={f.placeholder || ""}
                          onChange={(s) => setField(f.id, { placeholder: s })}
                          placeholder={DEFAULT_PLACEHOLDER_BY_TYPE[f.type]}
                        />
                      </div>
                    </SubRow>
                  )}
                </div>
              ))}
            </div>

            <ChipRow role="group" aria-label="Add a field of a given type">
              {FIELD_TYPES.map((t) => (
                <Chip
                  key={t.value}
                  type="button"
                  aria-label={`Add ${t.label} field`}
                  disabled={v.fields.length >= MAX_FIELDS}
                  onClick={() => addField(t.value)}
                >
                  + {t.label}
                </Chip>
              ))}
            </ChipRow>
            {v.fields.length >= MAX_FIELDS && (
              <Hint style={{ marginTop: 6 }}>Up to {MAX_FIELDS} fields.</Hint>
            )}
          </div>
        </EditorSection>

        {/* ISP-9999 (item 4): "the ADVANCED OPTIONS section is empty -> remove
            it entirely." Submit button + Responses now render as always-visible
            top-level sections instead of behind a collapsed accordion. */}
        <EditorSection heading="Submit button" dense>
          <TextInputField
            label="Button label"
            value={v.submitLabel}
            onChange={(s) => patch({ submitLabel: s })}
            counterMax={24}
            error={validateField(v.submitLabel, [maxLength(24, "Keep the label short.")])}
            placeholder="Send"
          />
          <ColorPopup
            title="Accent Color"
            background={v.submitColor}
            handleChangeBackgroundColor={(c) => patch({ submitColor: c })}
            resetBackgroundColor={() => patch({ submitColor: FORM_DEFAULT.submitColor })}
            showSlider={false}
          />
        </EditorSection>
        <EditorSection heading="Responses" dense>
          <Toggle
            label="Email me each response"
            checked={v.emailEachResponse}
            onChange={(b) => patch({ emailEachResponse: b })}
          />
          <Hint>
            Responses land in your Forms inbox — visitors never see each other's
            answers.
          </Hint>
        </EditorSection>
        {/* fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
            reuses the SAME QuadStepper Text/Image already use for their
            Padding/Margin control (BlockEditorPane/controls.jsx). */}
        <EditorSection heading="Spacing" dense>
          <QuadStepper label="Margin" value={v.margin} onChange={setMarginSide} />
        </EditorSection>
      </BlockEditorPane>
    </DndProvider>
  );
};

export default FormEditor;
